import { Component, Input, OnDestroy, OnInit, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngxs/store';
import { merge, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { NonConformanceState } from 'src/app/non-conformance-user/state/non-conformance.state';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { CommentEntityTypes } from '../enums/comment-entity-types.enum';
import { IComment } from '../model/comment.model';
import { ClearComments, LoadComments } from '../state/comments.actions';
import { CommentsState } from '../state/comments.state';

@Component({
  selector: 'app-comments-list',
  templateUrl: './comments-list.component.html',
  styleUrls: ['./comments-list.component.scss'],
  standalone: false,
})
export class CommentsListComponent implements OnInit, OnDestroy {
  private readonly injector = inject(Injector);

  @Input() entityId: string;
  @Input() entityType: number;
  @Input() isInPopup = false;
  @Input() adminPrivilege: string;
  @Input() showDeleteButton = true;

  canEditOwnComment = false;
  canDeleteOwnComment = false;
  isPOMode = false;

  private commentsForPopup = select(CommentsState.getCommentsForPopup);
  private commentsForPage = select(CommentsState.getComments);

  sortOrder: 'asc' | 'desc' = 'desc';
  commentsSorted: IComment[] = [];

  private subscriptions = new Subscription();

  constructor(private store: Store) {}

  ngOnInit(): void {
    const usePopup = this.isInPopup;
    this.subscriptions.add(
      merge(
        toObservable(this.commentsForPopup, { injector: this.injector }).pipe(
          map((c) => ({ comments: c, usePopup: true }))
        ),
        toObservable(this.commentsForPage, { injector: this.injector }).pipe(
          map((c) => ({ comments: c, usePopup: false }))
        )
      )
        .pipe(
          filter(({ usePopup: isPopup }) => isPopup === usePopup),
          tap(({ comments }) => {
            this.commentsSorted = this.sortComments(comments ?? []);
          })
        )
        .subscribe()
    );
    this.loadComments();
    const isPO = this.store.selectSnapshot(TenantUserAccessState.userAccess).isProductOwner;
    const ifNonConformance = this.entityType === CommentEntityTypes.NON_CONFORMANCE;
    const ifSubtask = this.entityType === CommentEntityTypes.SUBTASK;
    const ifOrder = this.entityType === CommentEntityTypes.ORDER;
    if (ifNonConformance) {
      const userResponsibility = this.store.selectSnapshot(
        NonConformanceState.getUserResponsibility
      );
      this.canEditOwnComment = userResponsibility.isResponsibleForClosing;
      this.canDeleteOwnComment = userResponsibility.isResponsibleForClosing;
      this.isPOMode = isPO;
    }
    if (ifSubtask) {
      this.canEditOwnComment = true;
      this.canDeleteOwnComment = false;
      this.isPOMode = false;
    }
    if (ifOrder) {
      this.canEditOwnComment = true;
      this.canDeleteOwnComment = true;
      this.isPOMode = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (!this.isInPopup) {
      this.store.dispatch(new ClearComments());
    }
  }

  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.commentsSorted = this.sortComments(this.commentsSorted);
  }

  private sortComments(comments: IComment[]): IComment[] {
    return [...comments].sort((a, b) =>
      this.sortOrder === 'desc'
        ? new Date(b.created).getTime() - new Date(a.created).getTime()
        : new Date(a.created).getTime() - new Date(b.created).getTime()
    );
  }

  private loadComments(): void {
    this.store.dispatch(new LoadComments(this.entityType, this.entityId, this.isInPopup));
  }
}
